import { FollowButtonAction, FollowType, unionArray } from "@product/scmp-sdk";

import type { NotificationCenterCompletionEvents } from "~/components/tracking/google-analytics/types";
import type { FollowClickEventParameters } from "~/components/tracking/google-analytics-4/types";
import { typedCapitalize } from "~/lib/utils";

import { FollowTypeKeyMapping } from "./consts";
import type { FollowButtonSource, FollowPayload, GetUserFollowList } from "./types";
import { followButtonSources } from "./types";

export const generateNewList = (
  previousFollowList: GetUserFollowList["userFollowList"] | undefined,
  payload: FollowPayload,
) => {
  const { action, ids, type } = payload;
  const newFollowList = previousFollowList
    ? { ...previousFollowList }
    : { authors: [], sections: [], topics: [] };
  const typeName = FollowTypeKeyMapping[type];
  if (action === FollowButtonAction.Follow) {
    newFollowList[typeName] = unionArray(newFollowList[typeName], ids);
  } else {
    const unfollowEntityIds = new Set(ids);
    newFollowList[typeName] = newFollowList[typeName].filter(
      entityId => !unfollowEntityIds.has(entityId),
    );
  }
  return newFollowList;
};

export function computeCompletionAction(
  action: FollowButtonAction,
  type: FollowType,
  source?: string,
): NotificationCenterCompletionEvents["action"] {
  const isValidFollowButtonSource = (source: string): source is FollowButtonSource =>
    followButtonSources.includes(source as FollowButtonSource);

  return source && isValidFollowButtonSource(source)
    ? `Notification Center/${typedCapitalize(action)}/${source}/${typedCapitalize(type)}`
    : `Notification Center/${typedCapitalize(action)}/${typedCapitalize(type)}`;
}

export function computeGa4CustomizedParameters(
  entityIds: string | string[],
  type: FollowType,
  source: FollowButtonSource,
): Omit<FollowClickEventParameters, "action_type" | "package_name" | "user_type"> {
  const UAFollowButtonHomePageSources = new Set([
    "Top Story 1",
    "Homepage_feature_package",
    "Homepage_gba",
    "HP Section Block",
  ]);
  const computedTriggerPoint = (() => {
    if (UAFollowButtonHomePageSources.has(source)) {
      return "Homepage_click";
    } else
      switch (source) {
        case "Article_topic_footer":
          return "Article_bottom";

        case "Trending Topics":
          return "trending_topics";

        case "Section":
          return "section";

        default:
          return source;
      }
  })();

  return {
    ...(type === FollowType.Author ? { author_id: entityIds[0] } : {}),
    ...(type === FollowType.Section ? { section_id: entityIds[0] } : {}),
    ...(type === FollowType.Topic ? { topic_id: entityIds[0] } : {}),
    follow_type: type,
    internal_campaign: "",
    trigger_point: computedTriggerPoint,
  };
}
